import React from 'react';

import ContentLayout from '@amzn/awsui-components-console/content-layout';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Header from '@amzn/awsui-components-console/header';
import Alert from '@amzn/awsui-components-console/alert';
import Button from '@amzn/awsui-components-console/button';

import TrackFilters from './components/TrackFilters';
import SavedTrackSearchesBar from './components/SavedTrackSearchesBar';
import SaveTrackSearchModal from './components/SaveTrackSearchModal';
import ActiveJobsTable from './components/ActiveJobsTable';
import HistoricalJobsTable from './components/HistoricalJobsTable';
import { useTrackFilters } from './hooks/useTrackFilters';
import { useTrackData } from './hooks/useTrackData';
import { TRACK_STRINGS } from './constants';

const TrackTranslationsPage: React.FC = () => {
  const filters = useTrackFilters();
  const trackData = useTrackData(filters.appliedFilters);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={TRACK_STRINGS.PAGE_DESCRIPTION}>
          {TRACK_STRINGS.PAGE_TITLE}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {filters.namespacesError && (
          <Alert type="error" header="Failed to load namespaces">
            Unable to load namespace options. Please refresh the page or try
            again later.
          </Alert>
        )}

        {filters.stringGroupsError && (
          <Alert type="error" header="Failed to load string groups">
            Unable to load string group options. The &quot;All&quot; filter is
            still available, but specific group selection may not work.
          </Alert>
        )}

        <TrackFilters
          namespace={filters.namespace}
          onNamespaceChange={filters.setNamespace}
          stringGroup={filters.stringGroup}
          onStringGroupChange={filters.setStringGroup}
          dateRange={filters.dateRange}
          onDateRangeChange={filters.setDateRange}
          onApply={filters.applyFilters}
          onSaveSearch={filters.openSaveModal}
          namespaceOptions={filters.namespaceOptions}
          stringGroupOptions={filters.stringGroupOptions}
          loading={filters.namespacesLoading}
          stringGroupsLoading={filters.stringGroupsLoading}
        />

        {/* Saved searches sit directly below the filter card, per track.html.
            Persisted in the users DDB table via GetUserPreference/UpdateUserPreference. */}
        <SavedTrackSearchesBar onLoadSearch={filters.loadSavedSearch} />

        <SaveTrackSearchModal
          visible={filters.saveModalVisible}
          name={filters.saveSearchName}
          onNameChange={filters.setSaveSearchName}
          onConfirm={filters.confirmSaveSearch}
          onCancel={filters.cancelSaveSearch}
          isSaving={filters.isSaving}
          errorText={filters.duplicateError}
        />

        {filters.appliedFilters.namespace && (
          <SpaceBetween size="l">
            {trackData.activeError && (
              <Alert
                type="error"
                header={TRACK_STRINGS.ERROR_ACTIVE_HEADER}
                action={
                  <Button onClick={() => trackData.retryActiveJobs()}>
                    Retry
                  </Button>
                }
              >
                {TRACK_STRINGS.ERROR_BODY}
              </Alert>
            )}
            <ActiveJobsTable
              items={trackData.activeError ? [] : trackData.activeJobs}
              loading={trackData.activeJobsLoading}
              isLoadingMore={trackData.activeIsLoadingMore}
              hasMore={trackData.activeHasMore}
              onLoadMore={trackData.loadMoreActiveJobs}
            />

            {trackData.historicalError && (
              <Alert
                type="error"
                header={TRACK_STRINGS.ERROR_HISTORICAL_HEADER}
                action={
                  <Button onClick={() => trackData.retryHistoricalJobs()}>
                    Retry
                  </Button>
                }
              >
                {TRACK_STRINGS.ERROR_BODY}
              </Alert>
            )}
            <HistoricalJobsTable
              items={trackData.historicalError ? [] : trackData.historicalJobs}
              loading={trackData.historicalJobsLoading}
              isLoadingMore={trackData.historicalIsLoadingMore}
              hasMore={trackData.historicalHasMore}
              onLoadMore={trackData.loadMoreHistoricalJobs}
            />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default TrackTranslationsPage;
